// @file Make posts sortable by drag and drop on Map.
import { __ } from '@@/bits/intl'
import { isPostPinned } from '@@/bits/post_state'
import type { PostDragEvent } from '@@/bits/surface_sortable'
import { isDraggedToBottomOfGroup, isDraggedToTopOfGroup, isElementPostGroup } from '@@/bits/surface_sortable'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { useForceChangeCursor } from '@@/vuecomposables/force_change_cursor'
import { debounce } from 'es-toolkit'
import { storeToRefs } from 'pinia'
import type Sortable from 'sortablejs'
import type { SortableEvent } from 'sortablejs'

// TODO: Consolidate this with the one in app/javascript/bits/surface_sortable.ts
// Right now we're using different data attributes so we can't reuse the same function.
const toPostDragEvent = (e: SortableEvent): PostDragEvent => {
  const postCid = e.item.dataset.postCid ?? ''
  const groupIdAsNumber = parseInt(e.to.dataset.groupId ?? '')
  const groupId = Number.isNaN(groupIdAsNumber) ? e.to.dataset.groupId ?? '' : groupIdAsNumber
  if (postCid === '' || groupId === '') {
    return {}
  }
  const newIndex = e.newIndex ?? -1
  return { postCid, groupId, newIndex }
}

export function useSurfacePostsDragSortableMap({
  onDragStart,
  onDragEnd,
}: {
  onDragStart?: () => void
  onDragEnd?: (e: PostDragEvent) => void
}): {
  onPostDragStart: typeof onPostDragStart
  onPostDragMove: typeof onPostDragMove
  onPostDragEnd: typeof onPostDragEnd
} {
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfaceMapStore = useSurfaceMapStore()
  const surfaceSectionsStore = useSurfaceSectionsStore()

  const { postEntitiesByCid } = storeToRefs(useSurfacePostsStore())
  const { sectionEntities } = storeToRefs(surfaceSectionsStore)
  const { areWishesManuallySorted } = storeToRefs(useSurfaceWishArrangementStore())
  const { forceChangeCursor, resetCursor } = useForceChangeCursor()

  const debouncedShowReorderErrorSnackbar = debounce(() => {
    globalSnackbarStore.setSnackbar({
      message: __('Cannot reorder sorted posts'),
      notificationType: SnackbarNotificationType.error,
    })
  }, 250)

  const onPostDragStart = (): void => {
    onDragStart?.()
    forceChangeCursor('grabbing')
    surfaceMapStore.startDraggingPosts()
  }

  const onPostDragMove = (e: Sortable.MoveEvent): boolean => {
    const isDraggingInSameGroup = e.from === e.to
    if (isDraggingInSameGroup && !areWishesManuallySorted.value) {
      debouncedShowReorderErrorSnackbar()
      return false
    }

    const draggedPostElement = e.dragged
    const relatedElement = e.related // this can either be a post or a group (if the group doesn't have posts)

    // related element is a group -> we are dragging into an empty group. we can drag there.
    if (isElementPostGroup(relatedElement)) {
      return true
    }

    // related element is a post -> we can drag there conditionally
    const draggedPostId = draggedPostElement.dataset.postCid
    const relatedPostId = relatedElement.dataset.postCid
    const draggedPost = draggedPostId == null ? undefined : postEntitiesByCid.value[draggedPostId]
    const relatedPost = relatedPostId == null ? undefined : postEntitiesByCid.value[relatedPostId]

    if (draggedPost == null || relatedPost == null) {
      return false
    }

    const targetGroup = sectionEntities.value[relatedPost?.wall_section_id ?? -1]
    if (targetGroup?.id == null) {
      return false
    }

    if (
      // if dragged is pinned and incoming group only has unpinned posts and we are dragging to the top of the group, D&D is OK
      (isPostPinned(draggedPost) && !surfaceSectionsStore.hasPinnedPosts(targetGroup.id) && isDraggedToTopOfGroup(e)) ||
      // conversely, if post is unpinned and incoming group is entirely pinned, and we are dragging to the bottom, D&D is OK
      (!isPostPinned(draggedPost) &&
        !surfaceSectionsStore.hasUnpinnedPosts(targetGroup.id) &&
        isDraggedToBottomOfGroup(e))
    ) {
      return true
    }

    // otherwise, pinned posts can only be dragged to pinned posts. and same for unpinned posts
    if (
      (isPostPinned(draggedPost) && !isPostPinned(relatedPost)) ||
      (!isPostPinned(draggedPost) && isPostPinned(relatedPost))
    ) {
      return false
    }

    return true
  }

  const onPostDragEnd = (e: SortableEvent): void => {
    onDragEnd?.(toPostDragEvent(e))
    resetCursor()
    surfaceMapStore.stopDraggingPosts()
    surfaceMapStore.highlightPostMarker({ postCid: null })
  }

  return {
    onPostDragStart,
    onPostDragMove,
    onPostDragEnd,
  }
}
